import { EMPTY_TABLE } from "./markdown-table";

export interface SlashCommand {
  id: string;
  key: string;
  label: string;
  detail: string;
  keywords: readonly string[];
  insert: string;
  select?: readonly [number, number];
}

export interface SlashContext {
  slashIndex: number;
  query: string;
}

export const SLASH_COMMANDS: readonly SlashCommand[] = [
  {
    id: "text",
    key: "TXT",
    label: "Plain text",
    detail: "paragraph",
    keywords: ["normal", "paragraph"],
    insert: "",
  },
  {
    id: "heading-1",
    key: "H1",
    label: "Heading 1",
    detail: "# title",
    keywords: ["title", "header"],
    insert: "# ",
  },
  {
    id: "heading-2",
    key: "H2",
    label: "Heading 2",
    detail: "## section",
    keywords: ["subtitle", "header"],
    insert: "## ",
  },
  {
    id: "heading-3",
    key: "H3",
    label: "Heading 3",
    detail: "### section",
    keywords: ["subtitle", "header"],
    insert: "### ",
  },
  {
    id: "bullet-list",
    key: "■",
    label: "Bullet list",
    detail: "- item",
    keywords: ["unordered", "list"],
    insert: "- ",
  },
  {
    id: "numbered-list",
    key: "1.",
    label: "Numbered list",
    detail: "1. item",
    keywords: ["ordered", "list"],
    insert: "1. ",
  },
  {
    id: "task",
    key: "☐",
    label: "Task",
    detail: "- [ ] item",
    keywords: ["todo", "checkbox", "list"],
    insert: "- [ ] ",
  },
  {
    id: "today",
    key: "DATE",
    label: "Today's date",
    detail: "current date or time",
    keywords: ["today", "date", "time", "timestamp"],
    insert: "",
  },
  {
    id: "quote",
    key: "│",
    label: "Quote",
    detail: "> text",
    keywords: ["blockquote", "callout"],
    insert: "> ",
  },
  {
    id: "bold",
    key: "B",
    label: "Bold text",
    detail: "**text**",
    keywords: ["strong", "format"],
    insert: "**bold text**",
    select: [2, 11],
  },
  {
    id: "italic",
    key: "I",
    label: "Italic text",
    detail: "_text_",
    keywords: ["emphasis", "format"],
    insert: "_italic text_",
    select: [1, 12],
  },
  {
    id: "inline-code",
    key: "`",
    label: "Inline code",
    detail: "`code`",
    keywords: ["monospace", "format"],
    insert: "`code`",
    select: [1, 5],
  },
  {
    id: "code-block",
    key: "```",
    label: "Code block",
    detail: "fenced block",
    keywords: ["fence", "snippet"],
    insert: "```\n\n```",
    select: [4, 4],
  },
  {
    id: "table",
    key: "▦",
    label: "Table",
    detail: "2 columns × 1 row",
    keywords: ["grid", "columns", "rows"],
    insert: EMPTY_TABLE,
    select: [2, 10],
  },
  {
    id: "link",
    key: "↗",
    label: "Link",
    detail: "[text](url)",
    keywords: ["url", "hyperlink"],
    insert: "[link text](https://)",
    select: [1, 10],
  },
  {
    id: "divider",
    key: "─",
    label: "Divider",
    detail: "---",
    keywords: ["rule", "separator"],
    insert: "---",
  },
  {
    id: "ai",
    key: "AI",
    label: "Ask AI",
    detail: "write here",
    keywords: ["assistant", "write", "generate", "draft", "rewrite", "llm", "gpt", "claude", "prompt"],
    insert: "",
  },
] as const;

/** Match a Notion-style slash command only when it begins the current line. */
export function getSlashContext(textBeforeCursor: string): SlashContext | null {
  const match = /^(\s*)\/([\w -]*)$/.exec(textBeforeCursor);
  if (!match) return null;
  return {
    slashIndex: match[1]?.length ?? 0,
    query: (match[2] ?? "").trimStart().toLocaleLowerCase(),
  };
}

/**
 * `/a` or `/ai` after a space, anywhere in a line: Ask AI is the one command that works
 * mid-line too. A lone slash isn't enough, so prose and paths don't open a menu.
 */
export function getAiSlashContext(textBeforeCursor: string): SlashContext | null {
  const match = /(^|\s)\/(ai?)$/i.exec(textBeforeCursor);
  if (!match) return null;
  return { slashIndex: match.index + match[1]!.length, query: match[2]!.toLocaleLowerCase() };
}

export function filterSlashCommands(query: string): readonly SlashCommand[] {
  const typed = query.trim().toLocaleLowerCase();
  const terms = typed.split(/\s+/).filter(Boolean);
  if (terms.length === 0) return SLASH_COMMANDS;
  const matches = SLASH_COMMANDS.filter((command) => {
    const haystack = `${command.id} ${command.label} ${command.detail} ${command.key} ${command.keywords.join(" ")}`
      .toLocaleLowerCase();
    return terms.every((term) => haystack.includes(term));
  });
  // A command named by what is typed comes first: /ai is Ask AI, not plAIn text.
  const named = (command: SlashCommand) => (command.id.startsWith(typed) ? 0 : 1);
  return matches.sort((a, b) => named(a) - named(b));
}
